/**
 * Auth Zustand store — authentication state, session initialization,
 * business/outlet selection, and logout.
 *
 * Persisted fields (via Zustand `persist`):
 *  - token                 — JWT
 *  - currentBusinessId     — last active business UUID
 *  - currentOutletId       — last active outlet UUID
 *
 * Non-persisted fields (rebuilt from API on each app start):
 *  - user, organization, businesses, currentBusiness, currentOutlet
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";
import { authApi } from "../api/auth";
import { businessesApi } from "../api/businesses";
import { outletsApi } from "../api/outlets";
import { saveToken, clearToken, getToken, onSessionEnded } from "../api/client";
import { permissionsFromToken } from "../lib/jwt";
import { queryClient } from "../lib/queryClient";
import type { AuthUser, Organization, Business, Outlet } from "../types/models";

interface AuthState {
  // Auth fields
  token: string | null;
  user: AuthUser | null;
  organization: Organization | null;

  // Permission strings for the current user (from GET /auth/session).
  // Empty for owner/admin (they bypass) and for roles with no catalog entry.
  permissions: string[];

  // Business/outlet fields
  currentBusiness: Business | null;
  currentOutlet: Outlet | null;
  allBusinesses: Business[] | null;
  currentBusinessId: string | null;
  currentOutletId: string | null;

  // Loading flags
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitialized: boolean;

  // Actions
  setAuth(token: string, user: AuthUser, org: Organization, businesses?: Business[], currentBusinessId?: string | null, currentOutletId?: string | null): void;
  setCurrentBusiness(business: Business): void;
  setCurrentOutlet(outlet: Outlet): void;
  setAllBusinesses(businesses: Business[]): void;
  logout(): void;
  /** The server ended this login (401): drop it and go to the sign-in page. */
  endSession(): void;
  initialize(): Promise<void>;
  switchBusiness(businessId: string): Promise<void>;
  resolveBusinessAndOutlet(tenantId?: string): Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      token: null,
      user: null,
      organization: null,
      permissions: [],
      currentBusiness: null,
      currentOutlet: null,
      allBusinesses: null,
      currentBusinessId: null,
      currentOutletId: null,
      isAuthenticated: false,
      isLoading: false,
      isInitialized: false,

      // setAuth
      setAuth(token, user, org, businesses, currentBusinessIdFromServer, currentOutletIdFromServer) {
        saveToken(token);

        // A sign-in starts a new session, as logout ends one, so drop the cache
        // here too (#96). Someone signing in over another person's session never
        // passes through logout, and most query keys are scoped by business or
        // outlet rather than by user: they would be shown what the last person
        // fetched, which their own permissions may not allow.
        queryClient.clear();
        console.log('[store] setAuth called, token: present, businesses:', businesses?.length || 0);
        console.log('[store] Server IDs - businessId:', currentBusinessIdFromServer || 'none', ', outletId:', currentOutletIdFromServer || 'none');
        console.log('[store] businesses:', JSON.stringify(businesses?.map(b => ({ id: b.id, name: b.name, is_active: b.is_active }))));

        // Determine which business to set as current
        let currentBusiness: Business | null = null;
        let resolvedBusinessId: string | null = currentBusinessIdFromServer || null;

        // If server gave us a business ID, find it
        if (resolvedBusinessId && businesses) {
          currentBusiness = businesses.find((b) => b.id === resolvedBusinessId) || null;
          console.log('[store] Found business from server:', currentBusiness?.name || 'not found');
        }

        // Fallback: if only one business, auto-select it
        if (!currentBusiness && businesses?.length === 1) {
          currentBusiness = businesses[0];
          resolvedBusinessId = currentBusiness.id;
          console.log('[store] Auto-selecting single business:', currentBusiness.name, 'id:', resolvedBusinessId);
        } else if (!currentBusiness) {
          console.log('[store] No business selected. businesses:', businesses?.length, 'currentBusiness:', (currentBusiness as Business | null)?.name);
        }

        set({
          token,
          user,
          organization: org,
          permissions: permissionsFromToken(token),
          isAuthenticated: true,
          allBusinesses: businesses || null,
          currentBusiness,
          currentBusinessId: resolvedBusinessId,
          currentOutletId: currentOutletIdFromServer || null,
        });

        // Explicitly persist to localStorage
        try {
          const persistedState = localStorage.getItem('admin_auth_store');
          const parsed = persistedState ? JSON.parse(persistedState) : {};
          if (!parsed.state) parsed.state = {};
          parsed.state.token = token;
          parsed.state.currentBusinessId = resolvedBusinessId;
          parsed.state.currentOutletId = currentOutletIdFromServer || null;
          localStorage.setItem('admin_auth_store', JSON.stringify(parsed));
          console.log('[store] Persisted to localStorage - businessId:', resolvedBusinessId, ', outletId:', currentOutletIdFromServer || null);
        } catch (e) {
          console.error('[store] Failed to persist:', e);
        }
      },

      // setCurrentBusiness
      setCurrentBusiness(business) {
        set({
          currentBusiness: business,
          currentBusinessId: business.id,
          currentOutlet: null,
          currentOutletId: null,
        });
      },

      // setCurrentOutlet
      setCurrentOutlet(outlet) {
        console.log('[store] setCurrentOutlet called, outlet:', outlet?.name);
        set({
          currentOutlet: outlet,
          currentOutletId: outlet.id,
        });

        // Explicitly persist to localStorage
        try {
          const persistedState = localStorage.getItem('admin_auth_store');
          const parsed = persistedState ? JSON.parse(persistedState) : {};
          if (!parsed.state) parsed.state = {};
          parsed.state.currentOutletId = outlet.id;
          localStorage.setItem('admin_auth_store', JSON.stringify(parsed));
          console.log('[store] Persisted setCurrentOutlet to localStorage:', outlet.id);
        } catch (e) {
          console.error('[store] Failed to persist setCurrentOutlet:', e);
        }
      },

      // setAllBusinesses
      setAllBusinesses(businesses) {
        const { currentBusinessId } = get();

        // Restore the previously-used business if it still exists
        let resolved: Business | null = null;
        if (currentBusinessId) {
          const found = businesses.find((b) => b.id === currentBusinessId);
          if (found) resolved = found;
        }

        set({
          allBusinesses: businesses,
          currentBusiness: resolved,
          currentBusinessId: resolved?.id ?? null,
        });
      },

      // logout
      logout() {
        authApi.signOut().catch(() => {
          // Ignore errors — local state is always cleared
        });

        clearToken();

        // Drop every cached response with it. Logout does not reload the page and
        // the QueryClient is module-scoped, so anything keyed without an org id
        // (subscription status, business settings, notifications) would otherwise
        // still be served to whoever logs in next while it is inside its
        // staleTime — including the plan feature flags that decide what they see.
        queryClient.clear();

        set({
          token: null,
          user: null,
          organization: null,
          permissions: [],
          currentBusiness: null,
          currentOutlet: null,
          allBusinesses: null,
          // Preserve IDs for next login
          currentBusinessId: null,
          currentOutletId: null,
          isAuthenticated: false,
        });
      },

      // endSession — the server already ended the login, so there is nobody
      // to sign out: clear what logout clears and start over at sign-in.
      endSession() {
        if (!get().isAuthenticated && !getToken()) return;
        clearToken();
        queryClient.clear();
        set({
          token: null,
          user: null,
          organization: null,
          permissions: [],
          currentBusiness: null,
          currentOutlet: null,
          allBusinesses: null,
          currentBusinessId: null,
          currentOutletId: null,
          isAuthenticated: false,
        });
        if (!window.location.pathname.startsWith("/login")) {
          window.location.assign("/login?reason=session_ended");
        }
      },

      // switchBusiness
      async switchBusiness(businessId: string) {
        try {
          console.log('[store] switchBusiness called, id:', businessId);
          const response = await authApi.switchBusiness(businessId);
          saveToken(response.token);

          // Use businesses from response if available, otherwise fall back to store's allBusinesses
          const businesses = response.businesses || get().allBusinesses;
          const business = businesses?.find((b) => b.id === businessId) || null;
          console.log('[store] Business found:', business?.name || 'not found');

          // Fetch outlets and auto-select one
          let selectedOutlet: Outlet | null = null;
          try {
            const outlets = await outletsApi.listOutlets(businessId);
            if (outlets && outlets.length > 0) {
              selectedOutlet = outlets.find(o => o.is_default && o.active) || 
                               outlets.find(o => o.is_default) || 
                               outlets.find(o => o.active) || 
                               outlets[0];
              console.log('[store] switchBusiness auto-selected outlet:', selectedOutlet?.name);
            }
          } catch (e) {
            console.error('[store] Failed to fetch outlets during switchBusiness:', e);
          }

          set({
            token: response.token,
            // New token is scoped to the switched business — refresh permissions.
            permissions: permissionsFromToken(response.token),
            allBusinesses: businesses, // Update if new list received
            currentBusiness: business,
            currentBusinessId: businessId,
            currentOutlet: selectedOutlet,
            currentOutletId: selectedOutlet?.id || null,
          });

          // Explicitly persist to localStorage
          try {
            const persistedState = localStorage.getItem('admin_auth_store');
            const parsed = persistedState ? JSON.parse(persistedState) : {};
            if (!parsed.state) parsed.state = {};
            parsed.state.token = response.token;
            parsed.state.currentBusinessId = businessId;
            parsed.state.currentOutletId = selectedOutlet?.id || null;
            localStorage.setItem('admin_auth_store', JSON.stringify(parsed));
            console.log('[store] Persisted switchBusiness to localStorage');
          } catch (e) {
            console.error('[store] Failed to persist switchBusiness:', e);
          }
        } catch (err) {
          console.error("Failed to switch business:", err);
          throw err;
        }
      },

      // resolveBusinessAndOutlet
      async resolveBusinessAndOutlet(tenantId?: string) {
        const { currentBusiness, allBusinesses } = get();

        // Cloud mode:
        // 1. Resolve business if not selected or if we should switch based on tenantId
        if (allBusinesses && allBusinesses.length > 0) {
          const activeBusinesses = allBusinesses.filter(b => b.is_active !== false);
          
          // Try to find matching business by tenantId if provided
          let matchedByTenantId: Business | null = null;
          if (tenantId) {
            matchedByTenantId = allBusinesses.find(b => 
              b.id.toLowerCase() === tenantId.toLowerCase() ||
              b.name.toLowerCase() === tenantId.toLowerCase() ||
              (b as any).business_slug?.toLowerCase() === tenantId.toLowerCase()
            ) || null;
          }

          const businessToSelect = (matchedByTenantId && matchedByTenantId.is_active !== false)
            ? matchedByTenantId
            : (activeBusinesses[0] || allBusinesses[0]);

          // We switch if no business is selected OR we matched a business by tenantId that is not the currently selected one
          const noBusinessSelected = !currentBusiness;
          const shouldSwitchToMatched = matchedByTenantId && (!currentBusiness || currentBusiness.id !== matchedByTenantId.id);

          if (businessToSelect && (noBusinessSelected || shouldSwitchToMatched)) {
            try {
              console.log('[store] Auto-switching to business:', businessToSelect.name);
              await get().switchBusiness(businessToSelect.id);
            } catch (e) {
              console.error('[store] Failed to auto-switch business:', e);
            }
          }
        }

        // 2. Ensure currentOutlet is loaded and selected
        const updatedState = get();
        if (updatedState.currentBusiness && !updatedState.currentOutlet) {
          try {
            const outlets = await outletsApi.listOutlets(updatedState.currentBusiness.id);
            console.log('[store] Fetched outlets:', outlets.length);
            if (outlets.length > 0) {
              let selectedOutlet: Outlet | null = null;
              if (updatedState.currentOutletId) {
                selectedOutlet = outlets.find(o => o.id === updatedState.currentOutletId) || null;
              }
              if (!selectedOutlet) {
                selectedOutlet = outlets.find(o => o.is_default && o.active) || 
                                 outlets.find(o => o.is_default) || 
                                 outlets.find(o => o.active) || 
                                 outlets[0];
              }
              if (selectedOutlet) {
                console.log('[store] Auto-selecting outlet:', selectedOutlet.name);
                get().setCurrentOutlet(selectedOutlet);
              }
            }
          } catch (e) {
            console.error('[store] Failed to auto-select outlet:', e);
          }
        }
      },

      // initialize
      async initialize() {
        console.log('[init] initialize() called, isInitialized:', get().isInitialized);

        // Prevent running multiple times
        if (get().isInitialized) {
          console.log('[init] Already initialized, skipping');
          return;
        }

        // Check Zustand's persist storage directly
        let storedToken: string | null = null;
        let persistedCurrentBusinessId: string | null = null;
        let persistedCurrentOutletId: string | null = null;
        try {
          const persistedState = localStorage.getItem('admin_auth_store');
          console.log('[init] localStorage raw value:', persistedState);
          const parsed = persistedState ? JSON.parse(persistedState) : null;
          console.log('[init] parsed state:', parsed?.state);
          storedToken = parsed?.state?.token || null;
          persistedCurrentBusinessId = parsed?.state?.currentBusinessId || null;
          persistedCurrentOutletId = parsed?.state?.currentOutletId || null;

          console.log('[init] extracted values - token:', storedToken ? 'present' : 'missing', ', businessId:', persistedCurrentBusinessId, ', outletId:', persistedCurrentOutletId);

          // No token - not authenticated, resolve immediately
          if (!storedToken) {
            console.log('[init] No token found, resolving as unauthenticated');
            set({
              isLoading: false,
              isInitialized: true,
              isAuthenticated: false,
              user: null,
              organization: null,
              permissions: [],
              currentBusiness: null,
              currentOutlet: null,
              allBusinesses: null,
            });
            return;
          }
        } catch (err) {
          console.warn('[init] Failed to read persisted state:', err);
        }

        // IMPORTANT: Ensure the API client has the token before making any calls
        if (storedToken) {
          saveToken(storedToken);
        }

        try {
          // Add timeout to prevent hanging
          console.log('[init] Fetching session...');
          const sessionPromise = authApi.getSession();
          const timeoutPromise = new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error('Timeout')), 5000)
          );
          const session = await Promise.race([sessionPromise, timeoutPromise]);
          console.log('[init] Session fetched successfully, is_valid:', session.is_valid, ', user:', session.user?.email);

          if (!session.is_valid) {
            throw new Error('Session invalid');
          }

          set({
            user: session.user ?? null,
            organization: session.organization ?? null,
            // Permissions come from the JWT (the canonical source in both cloud
            // and standalone modes); fall back to the session echo if present.
            permissions: permissionsFromToken(storedToken).length
              ? permissionsFromToken(storedToken)
              : session.permissions ?? [],
            isAuthenticated: true,
          });
          console.log('[init] Session has current_business_id:', session.current_business_id || 'none');

          // Load businesses (with timeout)
          try {
            console.log('[init] Fetching businesses...');
            const businessesPromise = businessesApi.listBusinesses();
            const businessesTimeoutPromise = new Promise<never>((_, reject) =>
              setTimeout(() => reject(new Error('Timeout')), 5000)
            );
            const businesses = await Promise.race([businessesPromise, businessesTimeoutPromise]);
            console.log('[init] Businesses fetched:', businesses.length, 'items');

            // Use the captured localStorage values AND session values since Zustand rehydration
            // may not have completed yet
            const resolvedBusinessId = session.current_business_id || persistedCurrentBusinessId;
            const resolvedOutletId = persistedCurrentOutletId;
            console.log('[init] Resolved IDs - businessId:', resolvedBusinessId, ', outletId:', resolvedOutletId);

            // Restore the previously-used business if it still exists
            let resolvedBusiness: Business | null = null;
            if (resolvedBusinessId) {
              const found = businesses.find((b) => b.id === resolvedBusinessId);
              if (found) resolvedBusiness = found;
              console.log('[init] Business lookup - resolvedBusiness:', resolvedBusiness?.name || 'not found');
            }

            set({
              allBusinesses: businesses,
              currentBusiness: resolvedBusiness,
              currentBusinessId: resolvedBusiness?.id ?? null,
            });
            console.log('[init] After set - currentBusiness:', get().currentBusiness?.name, ', currentBusinessId:', get().currentBusinessId);

            // Explicitly persist the outlet ID to localStorage since switchBusiness() clears it
            if (resolvedOutletId) {
              try {
                const persistedState = localStorage.getItem('admin_auth_store');
                const parsed = persistedState ? JSON.parse(persistedState) : {};
                if (!parsed.state) parsed.state = {};
                parsed.state.currentOutletId = resolvedOutletId;
                localStorage.setItem('admin_auth_store', JSON.stringify(parsed));
                console.log('[init] Persisted outlet ID to localStorage');
              } catch (e) {
                console.error("Failed to persist outlet ID:", e);
              }
            }

            // Ensure a business is always selected
            const { currentBusiness: currentBizAfterRestore } = get();
            const activeBusinesses = businesses;
            console.log('[init] Active businesses:', activeBusinesses.length, ', currentBusiness:', currentBizAfterRestore?.name || 'null');

            if (!currentBizAfterRestore && activeBusinesses.length > 0) {
              console.log('[init] No business selected, auto-selecting...');
              if (activeBusinesses.length === 1) {
                console.log('[init] Only one active business, auto-selecting:', activeBusinesses[0].name);
                try {
                  await get().switchBusiness(activeBusinesses[0].id);
                  // Restore outlet after switchBusiness
                  if (resolvedOutletId) {
                    try {
                      const outlets = await outletsApi.listOutlets(activeBusinesses[0].id);
                      const found = outlets.find((o) => o.id === resolvedOutletId);
                      if (found) {
                        get().setCurrentOutlet(found);
                        console.log('[init] Restored outlet after switchBusiness:', found.name);
                      }
                    } catch (e) {
                      console.error("Failed to restore outlet during init auto-select:", e);
                    }
                  }
                } catch (e) {
                  console.error("Failed to auto-select business during init:", e);
                }
              } else if (activeBusinesses.length > 1) {
                console.log('[init] Multiple active businesses, selecting first:', activeBusinesses[0].name);
                try {
                  await get().switchBusiness(activeBusinesses[0].id);
                  // Restore outlet after switchBusiness
                  if (resolvedOutletId) {
                    try {
                      const outlets = await outletsApi.listOutlets(activeBusinesses[0].id);
                      const found = outlets.find((o) => o.id === resolvedOutletId);
                      if (found) {
                        get().setCurrentOutlet(found);
                        console.log('[init] Restored outlet after switchBusiness:', found.name);
                      }
                    } catch (e) {
                      console.error("Failed to restore outlet during init auto-select:", e);
                    }
                  }
                } catch (e) {
                  console.error("Failed to select first business during init:", e);
                }
              }
            }

            // Ensure localStorage is synced with business ID
            try {
              const { currentBusiness: syncedBusiness } = get();
              if (syncedBusiness) {
                const persistedState = localStorage.getItem('admin_auth_store');
                const parsed = persistedState ? JSON.parse(persistedState) : {};
                if (!parsed.state) parsed.state = {};
                parsed.state.currentBusinessId = syncedBusiness.id;
                localStorage.setItem('admin_auth_store', JSON.stringify(parsed));
                console.log('[init] Synced business ID to localStorage:', syncedBusiness.id);
              }
            } catch (e) {
              console.error("Failed to persist business ID:", e);
            }
          } catch (err) {
            console.warn('[init] Failed to load businesses:', err);
          }

          // Restore outlet if business was restored
          const { currentBusiness, currentOutlet } = get();
          console.log('[init] Outlet restoration - currentBusiness:', currentBusiness?.name || 'null', ', currentOutlet:', currentOutlet?.name || 'null', ', persistedOutletId:', persistedCurrentOutletId);

          if (currentBusiness && !currentOutlet) {
            console.log('[init] Restoring outlet...');
            try {
              const outlets = await outletsApi.listOutlets(currentBusiness.id);
              console.log('[init] Outlets fetched for business:', outlets.length, 'items');
              const activeOutlets = outlets;
              console.log('[init] Active outlets:', activeOutlets.length);

              // First try to restore the previously selected outlet (captured at start of init)
              if (persistedCurrentOutletId) {
                console.log('[init] Looking for outlet with ID:', persistedCurrentOutletId);
                const outlet = activeOutlets.find((o) => o.id === persistedCurrentOutletId);
                if (outlet) {
                  console.log('[init] Found matching outlet:', outlet.name);
                  get().setCurrentOutlet(outlet);
                } else {
                  console.log('[init] Previous outlet not found in active outlets, auto-selecting...');
                  const fallbackOutlet = activeOutlets.find(o => o.is_default && o.active) || 
                                         activeOutlets.find(o => o.is_default) || 
                                         activeOutlets.find(o => o.active) || 
                                         activeOutlets[0];
                  if (fallbackOutlet) {
                    get().setCurrentOutlet(fallbackOutlet);
                  }
                }
              } else {
                console.log('[init] No previous outlet, auto-selecting...');
                const fallbackOutlet = activeOutlets.find(o => o.is_default && o.active) || 
                                       activeOutlets.find(o => o.is_default) || 
                                       activeOutlets.find(o => o.active) || 
                                       activeOutlets[0];
                if (fallbackOutlet) {
                  get().setCurrentOutlet(fallbackOutlet);
                }
              }

              // Ensure localStorage is synced after outlet is set
              const finalOutletId = get().currentOutletId;
              if (finalOutletId) {
                try {
                  const persistedState = localStorage.getItem('admin_auth_store');
                  const parsed = persistedState ? JSON.parse(persistedState) : {};
                  if (!parsed.state) parsed.state = {};
                  parsed.state.currentOutletId = finalOutletId;
                  localStorage.setItem('admin_auth_store', JSON.stringify(parsed));
                  console.log('[init] Synced outlet ID to localStorage:', finalOutletId);
                } catch (e) {
                  // ignore
                }
              }
            } catch (err) {
              console.warn('[init] Failed to load outlets:', err);
            }
          } else if (currentOutlet) {
            console.log('[init] Outlet already selected, skipping restoration');
          } else {
            console.log('[init] No business available for outlet restoration');
          }
        } catch (err) {
          console.error('[init] Session validation failed:', err);
          // Clear both storage locations
          clearToken();
          try {
            localStorage.removeItem('admin_auth_store');
          } catch { /* storage unavailable; state is reset below regardless */ }
          set({
            token: null,
            user: null,
            organization: null,
            permissions: [],
            currentBusiness: null,
            currentOutlet: null,
            allBusinesses: null,
            currentBusinessId: null,
            currentOutletId: null,
            isAuthenticated: false,
            isInitialized: true,
          });
        } finally {
          set({ isLoading: false, isInitialized: true });
          console.log('[init] Initialization complete. Final state - isAuthenticated:', get().isAuthenticated, ', currentBusiness:', get().currentBusiness?.name || 'null', ', currentOutlet:', get().currentOutlet?.name || 'null');
        }
      },
    }),
    {
      name: "admin_auth_store",
      partialize: (state) => ({
        token: state.token,
        currentBusinessId: state.currentBusinessId,
        currentOutletId: state.currentOutletId,
      }),
    }
  )
);

// A 401 on any signed-in call ends the session here (#159).
onSessionEnded(() => useAuthStore.getState().endSession());

// Convenience selectors
export const selectIsReady = (s: AuthState): boolean =>
  s.isInitialized && !s.isLoading && s.isAuthenticated;

export const selectCurrentBusinessId = (s: AuthState): string | null =>
  s.currentBusiness?.id ?? null;

export const selectCurrentOutletId = (s: AuthState): string | null =>
  s.currentOutlet?.id ?? null;

export const selectOrgRole = (s: AuthState): string | undefined =>
  s.user?.org_role;
